import { useConversation } from '../../contexts/ConversationContext';
import QueryHistorySidebar from './QueryHistorySidebar';
import SavedViewsSidebar from './SavedViewsSidebar';

interface SidebarProps {
  isOpen: boolean;
  onClose: () => void;
}

const suggestionPrompts = [
  {
    label: 'Returned items',
    prompt: 'Show me all returned items',
    icon: 'return',
  },
  {
    label: 'Items by stage',
    prompt: 'Show a breakdown of things by lifecycle stage',
    icon: 'chart',
  },
  {
    label: 'Holders overview',
    prompt: 'List holders by type',
    icon: 'users',
  },
  {
    label: 'Location analysis',
    prompt: 'Show things distribution by location type',
    icon: 'location',
  },
  {
    label: 'Processing status',
    prompt: 'What things are currently in processing?',
    icon: 'process',
  },
  {
    label: 'Recent activity',
    prompt: 'Show the most recently updated items',
    icon: 'clock',
  },
];

export default function Sidebar({ isOpen, onClose }: SidebarProps) {
  const { submitQuery } = useConversation();

  const handlePromptClick = (prompt: string) => {
    console.log('[Sidebar] Prompt clicked:', prompt);
    submitQuery(prompt);

    if (window.innerWidth < 768) {
      onClose();
    }
  };

  return (
    <>
      {/* Backdrop for mobile */}
      <div
        className={`fixed inset-0 bg-black/50 backdrop-blur-xs md:hidden transition-opacity duration-200 ${
          isOpen ? 'opacity-100' : 'opacity-0 pointer-events-none'
        }`}
        style={{ zIndex: 200 }}
        onClick={onClose}
      />

      {/* Sidebar */}
      <aside
        className={`fixed md:relative h-full bg-white border-r border-grey-medium/20 transition-transform duration-200 ease-out ${
          isOpen ? 'translate-x-0' : '-translate-x-full md:translate-x-0'
        } ${isOpen ? 'w-56 md:w-48' : 'w-0 md:w-0'} overflow-hidden flex-shrink-0`}
        style={{ zIndex: isOpen ? 300 : undefined }}
      >
        <div className="w-56 md:w-48 h-full flex flex-col">
          {/* Header */}
          <div className="px-3 py-2 border-b border-grey-medium/20 flex items-center justify-between md:hidden">
            <span className="font-light text-black text-sm">Menu</span>
            <button
              onClick={onClose}
              className="p-2 hover:bg-grey-light active:bg-grey-medium/20 transition-colors"
              aria-label="Close menu"
            >
              <svg className="w-5 h-5 text-grey-medium" fill="none" stroke="currentColor" strokeWidth={1.5} viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          {/* Quick prompts */}
          <div className="p-3">
            <h3 className="font-mono text-[10px] text-grey-medium uppercase tracking-wider mb-2">
              Quick Prompts
            </h3>
            <div className="space-y-0.5">
              {suggestionPrompts.map((item) => (
                <button
                  key={item.label}
                  onClick={() => handlePromptClick(item.prompt)}
                  className="w-full flex items-center gap-2 px-2 py-2 text-xs text-black hover:bg-grey-light active:bg-grey-medium/20 text-left transition-colors group"
                >
                  <div className="w-6 h-6 bg-grey-light flex items-center justify-center flex-shrink-0 group-hover:bg-mint/10 transition-colors">
                    <PromptIcon type={item.icon} />
                  </div>
                  <span className="font-light">{item.label}</span>
                </button>
              ))}
            </div>
          </div>

          {/* Divider */}
          <div className="border-t border-grey-medium/20" />

          {/* Saved Views */}
          <SavedViewsSidebar />

          {/* Divider */}
          <div className="border-t border-grey-medium/20" />

          {/* Query History */}
          <div className="flex-1 overflow-auto">
            <QueryHistorySidebar />
          </div>

          {/* Footer */}
          <div className="px-3 py-2 border-t border-grey-medium/20 bg-grey-light/50">
            <div className="flex items-center gap-2 font-mono text-[10px] text-grey-medium uppercase tracking-wider">
              <svg className="w-4 h-4 text-mint" fill="none" stroke="currentColor" strokeWidth={1.5} viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M13 10V3L4 14h7v7l9-11h-7z"
                />
              </svg>
              <span>GPT-OSS-120B</span>
            </div>
          </div>
        </div>
      </aside>
    </>
  );
}

function PromptIcon({ type }: { type: string }) {
  const className = "w-3.5 h-3.5 text-grey-medium group-hover:text-mint transition-colors";

  switch (type) {
    case 'return':
      return (
        <svg className={className} fill="none" stroke="currentColor" strokeWidth={1.5} viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" d="M9 15L3 9m0 0l6-6M3 9h12a6 6 0 010 12h-3" />
        </svg>
      );
    case 'chart':
      return (
        <svg className={className} fill="none" stroke="currentColor" strokeWidth={1.5} viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" d="M3 13h4v8H3zm7-5h4v13h-4zm7-5h4v18h-4z" />
        </svg>
      );
    case 'users':
      return (
        <svg className={className} fill="none" stroke="currentColor" strokeWidth={1.5} viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" d="M15 19.128a9.38 9.38 0 002.625.372 9.337 9.337 0 004.121-.952 4.125 4.125 0 00-7.533-2.493M15 19.128v-.003c0-1.113-.285-2.16-.786-3.07M15 19.128v.106A12.318 12.318 0 018.624 21c-2.331 0-4.512-.645-6.374-1.766l-.001-.109a6.375 6.375 0 0111.964-3.07M12 6.375a3.375 3.375 0 11-6.75 0 3.375 3.375 0 016.75 0zm8.25 2.25a2.625 2.625 0 11-5.25 0 2.625 2.625 0 015.25 0z" />
        </svg>
      );
    case 'location':
      return (
        <svg className={className} fill="none" stroke="currentColor" strokeWidth={1.5} viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" d="M15 10.5a3 3 0 11-6 0 3 3 0 016 0z" />
          <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 10.5c0 7.142-7.5 11.25-7.5 11.25S4.5 17.642 4.5 10.5a7.5 7.5 0 1115 0z" />
        </svg>
      );
    case 'process':
      return (
        <svg className={className} fill="none" stroke="currentColor" strokeWidth={1.5} viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" d="M16.023 9.348h4.992v-.001M2.985 19.644v-4.992m0 0h4.992m-4.993 0l3.181 3.183a8.25 8.25 0 0013.803-3.7M4.031 9.865a8.25 8.25 0 0113.803-3.7l3.181 3.182m0-4.991v4.99" />
        </svg>
      );
    case 'clock':
      return (
        <svg className={className} fill="none" stroke="currentColor" strokeWidth={1.5} viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" d="M12 6v6h4.5m4.5 0a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
      );
    default:
      return null;
  }
}
